import FormatCurrency from 'components/Common/FormatCurrency'
import BodyText from 'LuxKit/Typography/BodyText'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useSaleUnitFormatting from 'hooks/useSaleUnitFormatting'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { takeCurrentOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import Group from 'components/utils/Group'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import SolidCircleIcon from 'LuxKit/Icons/solid/SolidCircleIcon'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import useUserHasLoyaltyBenefit from 'loyalty/hooks/useUserHasLoyaltyBenefit'

interface Props {
  fromPrice: number
  duration: number
  withFlights?: boolean
  roomCount: number
  special?: boolean
  specialName?: string
  displayMemberPrice: boolean
  hasLoyaltyRoomUpgrade?: boolean
  saleUnit?: string
}

function OfferCalendarSidebarPriceBlurb(props: Props) {
  const {
    fromPrice,
    duration,
    withFlights,
    roomCount,
    special,
    specialName,
    displayMemberPrice,
    hasLoyaltyRoomUpgrade,
    saleUnit,
  } = props

  const intl = useIntl()
  const { formatSaleUnitLabel, getSaleUnitPluralNoun } = useSaleUnitFormatting()
  const defaultSpecialName = intl.formatMessage({
    id: '6oheyXsh6iJeLTKFemEy',
    defaultMessage: 'Limited time special',
    description: 'Default label for a limited time special promotion in calendar sidebar',
  })
  const resolvedSpecialName = specialName ?? defaultSpecialName
  const averagePrice = Math.ceil(fromPrice / (roomCount > 1 ? roomCount : duration))

  const displayUnit = saleUnit ?? 'room'

  const [tier] = useCurrentLoyaltyTier()
  const canLoyaltyUpgrade = useUserHasLoyaltyBenefit('hotel_upgrade')

  const priceElement = <b><FormatCurrency value={fromPrice} /></b>
  const priceWithAsterisk = <b><FormattedMessage
    id="j4Pr3CJCMiA3fwXE2ASW"
    defaultMessage="{price}*"
    description="Price with asterisk indicating flights included in sidebar"
    values={{ price: <FormatCurrency value={fromPrice} /> }}
  />
  </b>
  const averagePriceElement = <FormatCurrency value={averagePrice} />

  let priceBlurb: React.ReactNode
  if (roomCount > 1) {
    priceBlurb = <FormattedMessage
      id="MXSSiCntCrxGaSSewyPT"
      defaultMessage="{roomCount} {saleUnitLabel}, {nights, plural, one {# night} other {# nights}} from {price} total"
      description="Price blurb showing room count, sale unit, nights, and total price in calendar sidebar (multi-room)"
      values={{
        roomCount,
        saleUnitLabel: getSaleUnitPluralNoun(displayUnit),
        nights: duration,
        price: priceElement,
      }}
    />
  } else {
    priceBlurb = <FormattedMessage
      id="IPyCT10CvoAc36HDwMfZ"
      defaultMessage="{nights, plural, one {# night} other {# nights}} from {price} total"
      description="Price blurb showing nights and total price in calendar sidebar (single room)"
      values={{
        nights: duration,
        price: priceElement,
      }}
    />
  }

  let priceBlurbWithFlights: React.ReactNode
  if (roomCount > 1) {
    priceBlurbWithFlights = <FormattedMessage
      id="QvhSqr21F_DijVdQqblr"
      defaultMessage="{roomCount} {saleUnitLabel}, {nights, plural, one {# night} other {# nights}} + round trip flights from {price} (2 adults)"
      description="Price blurb showing room count, sale unit, nights, flights, and total price in calendar sidebar (multi-room)"
      values={{
        roomCount,
        saleUnitLabel: getSaleUnitPluralNoun(displayUnit),
        nights: duration,
        price: priceWithAsterisk,
      }}
    />
  } else {
    priceBlurbWithFlights = <FormattedMessage
      id="twejUvQQYAlyAehxLIuW"
      defaultMessage="{nights, plural, one {# night} other {# nights}} + round trip flights from {price} (2 adults)"
      description="Price blurb showing nights, flights, and total price in calendar sidebar (single room)"
      values={{
        nights: duration,
        price: priceWithAsterisk,
      }}
    />
  }

  let averagePriceBlurb: React.ReactNode
  if (roomCount > 1 && saleUnit) {
    averagePriceBlurb = <FormattedMessage
      id="7s-BiTNCirv-oqSf3yp7"
      defaultMessage="Average {price}/{saleUnit}"
      description="Average price per localized sale unit in the calendar sidebar when the offer provides a sale unit"
      values={{
        price: averagePriceElement,
        saleUnit: formatSaleUnitLabel(saleUnit, { withLeadingSlash: false }),
      }}
    />
  } else if (roomCount > 1) {
    averagePriceBlurb = <FormattedMessage
      id="AIZmRyOOzvRUMapw_Gjw"
      defaultMessage="Average {price}/room"
      description="Average price per room in the calendar sidebar when the offer does not provide a sale unit"
      values={{ price: averagePriceElement }}
    />
  } else {
    averagePriceBlurb = <FormattedMessage
      id="ze4LxHvGjcB5JBrGJzd7"
      defaultMessage="Average {price}/night"
      description="Average price per night in the calendar sidebar when the booking only has one room"
      values={{ price: averagePriceElement }}
    />
  }

  return (
    <Group
      direction="vertical"
      gap={4}
      tabletDirection="horizontal"
      tabletHorizontalAlign="space-between"
    >
      {!withFlights && <BodyText variant="small" colour="neutral-one">
        {displayMemberPrice &&
          <BodyText weight="bold" colour="lux-plus" variant="small" as="span" style={{ marginInlineEnd: '0.25em' }}>
            {intl.formatMessage(
              {
                id: 'Ozc8LMUkZZJFt5eCJKyw',
                defaultMessage: 'Showing {programName} member prices',
                description: 'Label indicating LuxPlus member prices are displayed in calendar sidebar',
              },
              { programName: LUXURY_PLUS.PROGRAM_NAME },
            )}
          </BodyText>}
        {priceBlurb}
        <CSSBreakpoint max="mobile" as="br" />
        <BodyText variant="small" colour="neutral-three" as="span" style={{ marginInlineStart: '0.25em' }}>
          {averagePriceBlurb}
        </BodyText>
      </BodyText>}
      {withFlights && <BodyText variant="small" colour="neutral-one">
        {displayMemberPrice &&
          <BodyText weight="bold" colour="lux-plus" variant="small" as="span" style={{ marginInlineEnd: '0.25em' }}>
            {intl.formatMessage(
              {
                id: 'R9ragplcwTdqytggkDI-',
                defaultMessage: 'Showing {programName} member prices',
                description: 'Label indicating LuxPlus member prices are displayed in calendar sidebar with flights',
              },
              { programName: LUXURY_PLUS.PROGRAM_NAME },
            )}
          </BodyText>}
        {priceBlurbWithFlights}
        <CSSBreakpoint max="mobile" as="br" />
        <BodyText variant="small" colour="neutral-three" as="span" style={{ marginInlineStart: '0.25em' }}>
          {averagePriceBlurb}
        </BodyText>
      </BodyText>}
      <Group direction="horizontal" gap={24}>
        {special && <CaptionTextBlock
          variant="medium"
          colour="neutral-two"
          startIcon={<SolidFireIcon colour="urgency" />}
        >
          {resolvedSpecialName}
        </CaptionTextBlock>}
        {hasLoyaltyRoomUpgrade && canLoyaltyUpgrade && <CaptionTextBlock
          variant="medium"
          colour="neutral-two"
          startIcon={<SolidCircleIcon colour={`tier-${tier}`} />}
        >
          {intl.formatMessage({
            id: 'Q6K89Xi9Vf_b6sIcUpHx',
            defaultMessage: 'Free upgrade available',
            description: 'Label indicating a free loyalty upgrade is available in sidebar price blurb',
          })}
        </CaptionTextBlock>}
      </Group>
    </Group>
  )
}

export default withOfferPageAndReduxState((currentState) => {
  const state = takeCurrentOfferState(currentState)

  return {
    duration: state.duration,
    withFlights: state.flights,
    rooms: state.rooms,
    displayMemberPrice: state.displayMemberPrice,
    saleUnit: state.offer?.saleUnit,
  }
}, OfferCalendarSidebarPriceBlurb)
